// Shared SigV4 request signer for our IAM-authed Lambda Function URLs.
//
// Both back-end proxies (the Coles fetch proxy and the AI pre-fill proxy) sit
// behind Lambda Function URLs with `authType AWS_IAM`, so every request must be
// SigV4-signed. We sign with the SAME short-lived Cognito credentials the app
// already uses for DynamoDB (see core/aws.js), so only signed-in Jexis users can
// invoke a proxy — there is no shared secret baked into the bundle.
//
// This module owns the AWS/signing mechanics; callers (core/coles-fetch.js,
// core/ai-prefill.js) own URL/body construction and response handling.

import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { config } from './config.js';
import { makeCognitoIdentityCredentialsProvider } from './aws.js';

/**
 * RFC 3986 percent-encoding, matching SigV4's canonical query encoding.
 *
 * `encodeURIComponent` leaves `! * ' ( )` unescaped, but SigV4 encodes them.
 * The wire URL's query MUST be byte-identical to the signer's canonical form,
 * or the signature won't match and the Function URL returns 403 — so we encode
 * those extra characters explicitly.
 */
function rfc3986(value) {
  return encodeURIComponent(value).replace(
    /[!*'()]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/**
 * Build a SigV4 signer bound to the current user's Cognito credentials, or
 * `null` if signing isn't possible. All Function-URL clients share this.
 *
 * @param idToken  the Cognito User Pool ID token for the signed-in user
 * @returns a `SignatureV4` instance for the `lambda` service in our region
 */
function makeSigner(idToken) {
  const credentials = makeCognitoIdentityCredentialsProvider(idToken);
  return new SignatureV4({
    service: 'lambda',
    region: config.region,
    credentials,
    sha256: Sha256,
  });
}

/**
 * Create a `fetch`-like function that SigV4-signs each request with HEADER-based
 * auth, bound to the signed-in user's credentials. Works for both GET (no body)
 * and POST (JSON body) requests to our Lambda Function URLs.
 *
 * IMPORTANT: Lambda Function URLs only support header-based SigV4 — query
 * (presigned-URL) auth is rejected with 403. So the signature/credentials go in
 * headers (`authorization`, `x-amz-date`, `x-amz-security-token`,
 * `x-amz-content-sha256`), and we send the URL byte-identical to what we signed
 * so the canonical request matches.
 *
 * We do NOT forward the signed `host` header to `fetch`: browsers manage `host`
 * themselves and reject attempts to set it. It stays in SignedHeaders and the
 * server derives it from the request, which matches what we signed.
 *
 * @param idToken  the Cognito User Pool ID token for the signed-in user
 * @returns async (endpoint, { method?, headers?, body? }) => Response
 */
export function createSignedFetch(idToken) {
  const signer = makeSigner(idToken);

  return async function signedFetch(endpoint, { method = 'GET', headers = {}, body } = {}) {
    const url = new URL(endpoint);

    // Decoded query params for the signer; it canonicalises (re-encodes) them.
    const query = {};
    for (const [k, v] of url.searchParams) query[k] = v;

    const request = new HttpRequest({
      method,
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname,
      query,
      // `host` is required in SignedHeaders; the body (if any) is hashed into
      // x-amz-content-sha256 by the signer, so it must be passed here verbatim.
      headers: { host: url.host, ...headers },
      ...(body === undefined ? {} : { body }),
    });

    const signed = await signer.sign(request);

    // Build the wire URL with the same RFC-3986 encoding SigV4 used to
    // canonicalise the query, so the bytes sent match the bytes signed.
    const qs = Object.entries(signed.query ?? query)
      .map(([k, v]) => `${rfc3986(k)}=${rfc3986(v)}`)
      .join('&');
    const wireUrl = `${url.protocol}//${url.host}${url.pathname}${qs ? `?${qs}` : ''}`;

    // Forward the signed headers, minus `host` (browser-controlled).
    const outHeaders = { ...signed.headers };
    delete outHeaders.host;

    return fetch(wireUrl, {
      method,
      headers: outHeaders,
      ...(body === undefined ? {} : { body }),
    });
  };
}
